import React, { useCallback } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd'

import { AddIcon } from '@chakra-ui/icons'
import { Box, Button, Text, useDisclosure } from '@chakra-ui/react'

import {
  useDefaultAiOperatorModelQuery,
  useDevicesWithTailgateConfiguredQuery,
  useEnabledAiOperatorModelsQuery,
} from '@/graphql/generated/hooks'
import { AiOperatorActionType } from '@/graphql/generated/schemas'
import { uuid } from '@/utils/helpers'

import { Criterion, CriterionOption } from '../types/types'
import {
  DEFAULT_MODEL_ID,
  containsNoUnauthorizedEntry,
  getAiOperatorModels,
  getAvailableCriteriaForItem,
  getDefaultActionForCriterion,
  getDefaultAiOperatorModel,
  getDeviceConfigurations,
} from '../utils/utils'
import { AiOperatorCriterionItem } from './AiOperatorCriterionItem'
import { AiOperatorNoCriteriaBanner } from './AiOperatorNoCriteriaBanner'
import { DefaultModelAlert } from './DefaultModelAlert'
import { DeviceMisconfiguredAlert } from './DeviceMisconfiguredAlert'
import { DevicesMisconfiguredModal } from './DevicesMisconfiguredModal'

interface AiOperatorCriteriaIProps {
  onChange: (
    criteria: Criterion[] | ((prev: Criterion[]) => Criterion[])
  ) => void
  criteria: Criterion[]
  criteriaOptions: Criterion[]
  disabled?: boolean
  doorIds: string[]
  isLoading?: boolean
}

const AiOperatorCriteria: React.FC<AiOperatorCriteriaIProps> = ({
  criteria,
  onChange,
  doorIds,
  disabled = false,
  criteriaOptions: availableCriteria,
  isLoading,
}) => {
  const { isImplementer } = usePermissions()
  const areValidDoorIds = doorIds && doorIds?.length > 0
  const { onOpen, isOpen, onClose } = useDisclosure()
  const { data: doorsData } = useDevicesWithTailgateConfiguredQuery({
    variables: { filter: { idIn: doorIds } },
    skip: !areValidDoorIds,
  })
  const { data: aiOperatorModelsData } = useEnabledAiOperatorModelsQuery({
    skip: !isImplementer,
  })
  const { data: defaultAiOperatorModelData } = useDefaultAiOperatorModelQuery({
    skip: !isImplementer,
  })

  const defaultAiOperatorModel = getDefaultAiOperatorModel(
    defaultAiOperatorModelData
  )
  const aiOperatorModels = getAiOperatorModels(aiOperatorModelsData)

  // region callbacks
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return

      onChange((prevCriteria) => {
        const items = Array.from(prevCriteria)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)
        return items
      })
    },
    [onChange]
  )

  const handleDelete = useCallback(
    (id: string) => {
      onChange((prevCriteria) => prevCriteria.filter((item) => item.id !== id))
    },
    [onChange]
  )

  const handleAddCriterion = useCallback(() => {
    if (availableCriteria.length === 0) return

    onChange((prevCriteria) => {
      const usedValues = new Set(prevCriteria.map((c) => c.criterion.id))
      const firstUnused = availableCriteria.find(
        (opt) => !usedValues.has(opt.id)
      )

      // don't add
      if (!firstUnused) return prevCriteria

      const newCriterion: Criterion = {
        id: uuid(),
        action: getDefaultActionForCriterion(firstUnused.criterion.description),
        criterion: firstUnused.criterion,
        detected: true,
        // undefined = use current default model (dynamic, not pinned)
        aiOperatorModelId: undefined,
      }

      return [...prevCriteria, newCriterion]
    })
  }, [availableCriteria, onChange])

  const handleUpdateCriterion = useCallback(
    (id: string, newCriterion: CriterionOption) => {
      onChange((prevCriteria) =>
        prevCriteria.map((item) =>
          item.id === id
            ? {
                ...item,
                criterion: newCriterion,
              }
            : item
        )
      )
    },
    [onChange]
  )

  const handleUpdateCriterionModel = useCallback(
    (id: string, modelId: string | undefined) => {
      // map DEFAULT_MODEL_ID to undefined to indicate using backend default
      const aiOperatorModelId =
        modelId === DEFAULT_MODEL_ID ? undefined : modelId

      onChange((prevCriteria) =>
        prevCriteria.map((item) =>
          item.id === id
            ? {
                ...item,
                aiOperatorModelId,
              }
            : item
        )
      )
    },
    [onChange]
  )

  const handleUpdateAction = useCallback(
    (id: string, newAction: AiOperatorActionType) => {
      onChange((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, action: newAction } : item
        )
      )
    },
    [onChange]
  )
  // endregion

  const misconfiguredDoors = getDeviceConfigurations(doorsData)
  const numberOfMisconfiguredDoors = misconfiguredDoors?.length || 0
  const hasAuthorizedEgressCriteria = containsNoUnauthorizedEntry(criteria)
  const shouldShowMisconfigurationAlert =
    numberOfMisconfiguredDoors > 0 && hasAuthorizedEgressCriteria

  const hasNoCriterion = criteria.length === 0 && !isLoading

  const usedCriteriaValues = new Set(criteria.map((c) => c.criterion.id))
  const hasUnusedOptions = availableCriteria.some(
    (opt) => !usedCriteriaValues.has(opt.id)
  )

  return (
    <Box mb={6} width='100%'>
      {shouldShowMisconfigurationAlert && (
        <DeviceMisconfiguredAlert
          count={numberOfMisconfiguredDoors}
          onClick={onOpen}
        />
      )}
      <DefaultModelAlert
        criteria={criteria}
        defaultAiOperatorModel={defaultAiOperatorModel}
        isImplementer={isImplementer}
      />
      <Text fontSize='16px' fontWeight='medium' letterSpacing='-0.8px' mb={2}>
        AI Operator Criteria
      </Text>
      <Text color='gray.600' fontSize='sm' mb={4}>
        Drag and drop to reorder the criteria. The top item will be checked
        first.
      </Text>

      {hasNoCriterion && (
        <AiOperatorNoCriteriaBanner
          hasNoCriterion={hasNoCriterion}
          message='There are no criteria for this rule'
        />
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='criteria-list'>
          {(provided) => (
            <Box
              {...provided.droppableProps}
              mb={4}
              ref={provided.innerRef}
              width='100%'
            >
              {criteria.map((item, index) => {
                const itemAvailableCriteria = getAvailableCriteriaForItem(
                  item,
                  criteria,
                  availableCriteria
                )

                return (
                  <Draggable draggableId={item.id} index={index} key={item.id}>
                    {(provided) => (
                      <AiOperatorCriterionItem
                        aiOperatorModels={aiOperatorModels}
                        availableCriteria={itemAvailableCriteria}
                        disabled={disabled}
                        handleDelete={handleDelete}
                        handleUpdateAction={handleUpdateAction}
                        handleUpdateCriterion={handleUpdateCriterion}
                        isImplementer={isImplementer}
                        item={item}
                        onAiOperatorModelChange={handleUpdateCriterionModel}
                        provided={provided}
                      />
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {!disabled && (
        <Button
          borderRadius='md'
          isDisabled={!hasUnusedOptions}
          leftIcon={<AddIcon />}
          onClick={handleAddCriterion}
          size='sm'
          variant='outline'
        >
          Add criteria{' '}
        </Button>
      )}
      <DevicesMisconfiguredModal
        devices={misconfiguredDoors}
        isOpen={isOpen}
        onClose={onClose}
      />
    </Box>
  )
}

export { AiOperatorCriteria }
